import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';

const KEY = (projectId: string) => ['projects', projectId, 'anforderungen'] as const;

export function useAnforderungen(projectId: string | undefined) {
  return useQuery({
    queryKey: KEY(projectId ?? ''),
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/anforderungen`),
    enabled: !!projectId,
  });
}

export function useCreateAnforderung(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`/api/v1/projects/${projectId}/anforderungen`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(projectId) }),
  });
}

export function useUpdateAnforderung(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`/api/v1/projects/${projectId}/anforderungen/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(projectId) }),
  });
}

export function useDeleteAnforderung(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/v1/projects/${projectId}/anforderungen/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(projectId) }),
  });
}

/**
 * Persistiert eine neue Reihenfolge: setzt `nr` jeder betroffenen Zeile auf ihre
 * 1-basierte Position. Die Nummer ergibt sich damit aus der Position (Drag&Drop),
 * ein eigenes Backend-Positionsfeld ist nicht noetig. Optimistisches Update haelt
 * die Liste waehrend des Requests stabil.
 *
 * Ein atomarer Bulk-Call (pm-api `/anforderungen/reorder`, eine TX mit NULL-Phase):
 * loest den Unique-Index `ux_anforderungen_project_nr` waehrend des Umsortierens
 * auf, daher kein 409 mehr durch ueberlappende Zwischenpositionen. nr als String.
 */
export function useReorderAnforderungen(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updates: { id: string; nr: number }[]) => {
      await apiClient.patch(`/api/v1/projects/${projectId}/anforderungen/reorder`, {
        items: updates.map((u) => ({ id: u.id, nr: String(u.nr) })),
      });
    },
    onMutate: async (updates: { id: string; nr: number }[]) => {
      await qc.cancelQueries({ queryKey: KEY(projectId) });
      const previous = qc.getQueryData(KEY(projectId));
      const nrById = new Map(updates.map((u) => [u.id, u.nr]));
      qc.setQueryData(KEY(projectId), (old: unknown) => {
        const list = (old as { data?: { id: string; nr: string | null }[] } | undefined)?.data;
        if (!Array.isArray(list)) return old;
        const next = list.map((row) =>
          nrById.has(row.id) ? { ...row, nr: String(nrById.get(row.id)) } : row,
        );
        return { ...(old as object), data: next };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) qc.setQueryData(KEY(projectId), ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY(projectId) }),
  });
}
